// IndexedDB-backed queue store — canon Phase 4. Falls back to in-memory if IDB is
// unavailable (private mode / old browsers) so the SDK never throws.
import type { QueueStore, QueuedEvent } from './types';

const DB_NAME = 'fixgarden_analytics';
const STORE = 'events';

function memoryStore(): QueueStore {
  const map = new Map<string, QueuedEvent>();
  return {
    async loadPending() {
      return [...map.values()];
    },
    async put(e) {
      map.set(e.envelope.event_id, e);
    },
    async remove(id) {
      map.delete(id);
    },
  };
}

export function createIndexedDbStore(): QueueStore {
  if (typeof indexedDB === 'undefined') return memoryStore();

  let dbPromise: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'envelope.event_id' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return dbPromise;
  };

  const run = async <T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  };

  return {
    async loadPending() {
      try {
        return (await run('readonly', (s) => s.getAll() as IDBRequest<QueuedEvent[]>)) ?? [];
      } catch {
        return [];
      }
    },
    async put(e) {
      try {
        await run('readwrite', (s) => s.put(e) as IDBRequest<IDBValidKey>);
      } catch {
        /* best effort */
      }
    },
    async remove(id) {
      try {
        await run('readwrite', (s) => s.delete(id) as IDBRequest<undefined>);
      } catch {
        /* best effort */
      }
    },
  };
}
